import { Modal, DatePicker, Form, Select, Avatar, Space, List, Typography, Tag, Collapse, Card, Tooltip, Button, Checkbox } from 'antd';
import { useState, useEffect } from 'react';
import { CloseCircleOutlined, DownOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { Route, CrewMember } from '../../RouteManagement/routingApi';
import { useRouteJobsQuery, useJobsForCreateRouteQuery } from '../../RouteManagement/routingQueries';
import { useSchedule } from '../ScheduleContext';
import { getInitials } from '../scheduleModel';
import RouteModalJobWorkAreas from './RouteModalJobWorkAreas';
import { formatDate, formatRelativeTime } from './scheduleCreateEventModel';
import { useCreateScheduledEventByJobMutation } from '../scheduleQueries';

const { Text } = Typography;

type Props = {
  open: boolean
  onClose: () => void
  route: Route | null
  onSave: (eventData: any) => void
  selectedDate?: Dayjs
}

const CreateEventFromRouteModal = ({ open, onClose, route, onSave, selectedDate: initialDate }: Props) => {
  const [form] = Form.useForm();
  const [selectedCrew, setSelectedCrew] = useState<CrewMember[]>([]);
  const defaultDate = initialDate || dayjs();
  const [eventDate, setEventDate] = useState<Dayjs | null>(defaultDate);
  const [crewLeadId, setCrewLeadId] = useState<number | null>(null);
  const [removedJobIds, setRemovedJobIds] = useState<Set<number>>(new Set());
  const [isCreating, setIsCreating] = useState(false);

  const { crew } = useSchedule();
  const { data: routeJobs } = useRouteJobsQuery(route?.id || 0);
  const { data: allJobs } = useJobsForCreateRouteQuery();
  const createEventMutation = useCreateScheduledEventByJobMutation();

  useEffect(() => {
    if (open && route) {
      const dateToUse = initialDate || dayjs();
      const crewMemberIds = route.crewMemberIds
        ? route.crewMemberIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
        : [];
      const routeCrewMembers = crewMemberIds.map(id => crew?.find(member => member.id === id)).filter(Boolean) as CrewMember[];

      setSelectedCrew(routeCrewMembers);
      setCrewLeadId(route.crewLeadId);
      setRemovedJobIds(new Set());
      setEventDate(dateToUse);

      form.setFieldsValue({
        eventDate: dateToUse,
        crewMembers: crewMemberIds,
      });
    }
  }, [open, route, crew, form, initialDate]);

  const handleClose = () => {
    const dateToUse = initialDate || dayjs();
    form.resetFields();
    setSelectedCrew([]);
    setEventDate(dateToUse);
    setCrewLeadId(null);
    setRemovedJobIds(new Set());
    setIsCreating(false);
    onClose();
  };

  const handleToggleJob = (jobId: number, checked: boolean) => {
    setRemovedJobIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    try {
      if (!route || !eventDate) {
        return;
      }

      setIsCreating(true);

      const activeJobs = (routeJobs || [])
        .filter(job => job.jobId !== null && !removedJobIds.has(job.jobId!))
        .sort((a, b) => a.sortIndex - b.sortIndex);

      const crewMemberIds = selectedCrew.map(m => m.id).join(',');

      const routeStartTime = dayjs(route.dayStartTime);
      let currentDateTime = eventDate
        .hour(routeStartTime.hour())
        .minute(routeStartTime.minute())
        .second(0);

      for (const routeJob of activeJobs) {
        const startDateTime = currentDateTime;
        const durationMinutes = routeJob.estimatedDurationInMinutes;
        const endDateTime = startDateTime.add(durationMinutes, 'minutes');

        await createEventMutation.mutateAsync({
          jobId: routeJob.jobId!,
          start: startDateTime.toISOString(),
          end: endDateTime.toISOString(),
          dayStartTime: null,
          estimatedDurationInMinutes: durationMinutes,
          crewMemberIds,
          crewLeadId,
          workareaIds: routeJob.workAreaIds || '',
          routeId: route.id,
          hasApprovedWorkareas: false,
          allDay: false,
          sendCrewLeadNotifications: false,
          frozen: false
        });

        currentDateTime = endDateTime;
      }

      if (onSave) {
        onSave({
          routeId: route.id,
          routeName: route.name,
          eventDate: eventDate,
          crewMembers: selectedCrew,
          crewLeadId: crewLeadId,
          jobs: activeJobs,
        });
      }

      handleClose();
    } catch (error) {
      setIsCreating(false);
    }
  };

  const handleCrewSelect = (value: number[] | undefined) => {
    if (!value || !Array.isArray(value)) {
      setSelectedCrew([]);
      return;
    }

    const newCrewMembers = value.map(id =>
      crew?.find(member => member.id === id)
    ).filter(Boolean) as CrewMember[];

    setSelectedCrew(newCrewMembers);

    if (!crewLeadId && newCrewMembers.length > 0) {
      const firstWithEmail = newCrewMembers.find(member => member.userEmail);
      if (firstWithEmail) {
        setCrewLeadId(firstWithEmail.id);
      }
    }
  };

  const removeCrewMember = (id: number) => {
    const updated = selectedCrew.filter(member => member.id !== id);
    setSelectedCrew(updated);
    form.setFieldsValue({ crewMembers: updated.map(m => m.id) });
    if (crewLeadId === id) {
      setCrewLeadId(null);
    }
  };

  const setCrewLead = (id: number) => {
    if (crewLeadId === id) {
      setCrewLeadId(null);
    } else {
      setCrewLeadId(id);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      onOk={handleSave}
      okText="Create Event"
      okButtonProps={{ loading: isCreating, disabled: isCreating }}
      cancelText="Cancel"
      cancelButtonProps={{ disabled: isCreating }}
      width={600}
      closable={!isCreating}
      maskClosable={!isCreating}
      title={
        <div>
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>
            {route?.name || 'Route Name'}
          </div>
          <div style={{ fontSize: '12px', fontWeight: 400, color: '#666', display: 'flex', gap: '12px', alignItems: 'center' }}>
            <span>Jobs {route?.jobsCount || 0}</span>
            <span>|</span>
            <span>Created {route?.createdAtUtc ? formatDate(route.createdAtUtc) : ''}</span>
            <span>|</span>
            <span>Last Edited {route?.updatedAtUtc ? formatRelativeTime(route.updatedAtUtc) : ''}</span>
          </div>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: '24px' }}
      >

        <div style={{ marginBottom: '24px' }}>
          <Text style={{ fontSize: '14px', display: 'block', marginBottom: '8px' }}>
            Event Date
          </Text>
          <Card>
            <DatePicker
              style={{ width: '100%' }}
              format="MMMM D, YYYY"
              value={eventDate}
              onChange={(date) => setEventDate(date)}
              allowClear={false}
            />
          </Card>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <Text style={{ fontSize: '14px', display: 'block', marginBottom: '8px' }}>
            Crew Members
          </Text>
          <Card>
            <Select
              showSearch
              placeholder="Search and select crew members"
              optionFilterProp="label"
              style={{ width: '100%', marginBottom: selectedCrew.length > 0 ? '16px' : '0' }}
              onChange={handleCrewSelect}
              value={selectedCrew?.map(member => member?.id).filter(Boolean) || []}
              allowClear
              mode="multiple"
              maxTagCount={3}
              maxTagTextLength={10}
            >
              {crew?.map((member) => (
                <Select.Option
                  key={member.id}
                  value={member.id}
                  label={member.name}
                >
                  <Space>
                    <Avatar
                      src={member.hasStockAvatar ? undefined : member.avatar}
                      size="small"
                      style={member.hasStockAvatar || !member.avatar ? { backgroundColor: member.color || '#d9d9d9', color: '#fff' } : undefined}
                    >
                      {getInitials(member.name)}
                    </Avatar>
                    <span>{member.name}</span>
                  </Space>
                </Select.Option>
              ))}
            </Select>

            {selectedCrew.length > 0 ? (
              <List
                size="small"
                dataSource={selectedCrew}
                renderItem={(member) => (
                  <List.Item
                    actions={[
                      !member.userEmail ? (
                        <Tooltip key="setLead" title="Crew member needs an email address to be a crew lead">
                          <Button
                            type="link"
                            size="small"
                            disabled
                            style={{
                              padding: 0,
                              cursor: 'not-allowed'
                            }}
                          >
                            Set as Crew Lead
                          </Button>
                        </Tooltip>
                      ) : (
                        <a
                          key="setLead"
                          onClick={() => setCrewLead(member.id)}
                          style={{ color: crewLeadId === member.id ? '#d4380d' : '#1890ff' }}
                        >
                          {crewLeadId === member.id ? "Remove Crew Lead" : "Set as Crew Lead"}
                        </a>
                      ),
                      <a key="remove" onClick={() => removeCrewMember(member.id)}>
                        <CloseCircleOutlined style={{ fontSize: '16px' }} />
                      </a>
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          src={member.hasStockAvatar ? undefined : member.avatar}
                          size="small"
                          style={member.hasStockAvatar || !member.avatar ? { backgroundColor: member.color || '#d9d9d9', color: '#fff' } : undefined}
                        >
                          {getInitials(member.name)}
                        </Avatar>
                      }
                      title={
                        <Space>
                          {member.name}
                          {crewLeadId === member.id && (
                            <Tag color="gold">Crew Lead</Tag>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : null}
          </Card>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <Text style={{ fontSize: '14px', display: 'block', marginBottom: '8px' }}>
            Jobs
          </Text>
          <Card>
            {routeJobs && routeJobs.length > 0 ? (
              (() => {
                const sortedJobs = routeJobs
                  .filter(rj => rj.jobId !== null)
                  .sort((a, b) => a.sortIndex - b.sortIndex);
                const checkedCount = sortedJobs.filter(rj => !removedJobIds.has(rj.jobId!)).length;

                return sortedJobs.map((routeJob) => {
                  const jobDetails = allJobs?.find(j => j.jobId === routeJob.jobId);
                  if (!jobDetails) return null;

                  const isChecked = !removedJobIds.has(routeJob.jobId!);
                  const isLastChecked = isChecked && checkedCount === 1;
                  const workAreaCount = routeJob.workAreaIds ? routeJob.workAreaIds.split(',').length : 0;
                  const estimatedHours = Math.round((routeJob.estimatedDurationInMinutes / 60) * 10) / 10;
                  const formattedEstimate = estimatedHours === 1 ? '1 Hour' : `${estimatedHours} Hour${estimatedHours !== 1 ? 's' : ''}`;

                  return (
                    <div
                      key={routeJob.id}
                      style={{
                        border: '1px solid #f0f0f0',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '8px',
                        backgroundColor: 'white',
                        opacity: isChecked ? 1 : 0.5
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
                            {jobDetails.jobName}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {jobDetails.customerDisplayName} | Est. {formattedEstimate}
                          </div>
                        </div>
                        <Checkbox
                          checked={isChecked}
                          disabled={isLastChecked}
                          onChange={(e) => handleToggleJob(routeJob.jobId!, e.target.checked)}
                        />
                      </div>
                      <Collapse
                        ghost
                        size="small"
                        expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} style={{ fontSize: '10px' }} />}
                        items={[
                          {
                            key: '1',
                            label: (
                              <span style={{ fontSize: '12px', color: '#666' }}>
                                {workAreaCount} Work Area{workAreaCount !== 1 ? 's' : ''}:
                              </span>
                            ),
                            children: <RouteModalJobWorkAreas jobId={routeJob.jobId!} workAreaIds={routeJob.workAreaIds || ''} />,
                          },
                        ]}
                        style={{ marginTop: '8px' }}
                      />
                    </div>
                  );
                });
              })()
            ) : (
              <div style={{
                textAlign: 'center',
                color: '#999',
                padding: '24px'
              }}>
                No jobs in route
              </div>
            )}
          </Card>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateEventFromRouteModal;
